import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum'
import { IMonitoringProvider } from '@sennder/senn-node-microfrontend-interfaces'
import { redactEmailAddress } from './utils'

type OptionalRumInitConfiguration = Omit<
  RumInitConfiguration,
  'applicationId' | 'clientToken' | 'site' | 'service' | 'env' | 'version'
>

type DatadogSite = RumInitConfiguration['site']

/**
 * Regex to extract service name and version from microfrontend script URL.
 * Example URL: https://cdn.sennder.cloud.com/npm/npm/incident-reporting-component/4b523eaf32f4f3f976a38cdfe0d7a9f63b6842a4/158.99e9c9868f3df18658be.js
 * Example URL: https://cdn.sennder.cloud.com/npm/npm/trip-mf/feature-branches/notify-users-fe/3d7b417bc5cc4b50c06877797d396a675a739d1e/616.dc9629522b4bb361107d.js
 */
const MICROFRONTEND_REGEX =
  /https:\/\/[^/]+\/npm\/([^/]+)\/.*?([a-f0-9]+)\/[^/]+\.js/

export default class DatadogMonitor implements IMonitoringProvider {
  private _canSendEvent: boolean
  private _maskSensitiveData: boolean
  private _enableServiceDetection: boolean

  constructor(
    appId: string,
    clientToken: string,
    service: string,
    site: DatadogSite = 'datadoghq.eu',
    env = 'dev',
    version = '1.0.0',
    // optional param to extend or rewrite datadog rum application init configs
    datadogRumAppOptions?: OptionalRumInitConfiguration,
    maskSensitiveData = true,
    enableServiceDetection = true
  ) {
    const message = (name: string): string => `${name} is not specified`
    if (!appId) {
      throw Error(message('Application id'))
    }
    if (!clientToken) {
      throw Error(message('Client token'))
    }
    if (!service) {
      throw Error(message('Service name'))
    }
    this._canSendEvent = true
    this._maskSensitiveData = maskSensitiveData
    this._enableServiceDetection = enableServiceDetection
    this.initDatadogRum(
      appId,
      clientToken,
      service,
      site,
      env,
      version,
      datadogRumAppOptions
    )
  }

  public sendError(error: Error, context?: object): void {
    datadogRum.addError(error, context)
  }

  public setUser(userId: string, userEmail: string): void {
    datadogRum.setUser({
      id: userId,
      email: redactEmailAddress(userEmail, this._maskSensitiveData),
    })
  }

  public addFeatureFlagEvaluation(key: string, value: unknown): void {
    datadogRum.addFeatureFlagEvaluation(key, value)
  }

  private initDatadogRum(
    applicationId: string,
    clientToken: string,
    service: string,
    site: DatadogSite,
    env: string,
    version: string,
    datadogRumAppOptions?: OptionalRumInitConfiguration
  ) {
    const initRumConfiguration: RumInitConfiguration = {
      applicationId,
      clientToken,
      site,
      service,
      env,
      version,
    }
    datadogRum.init({
      ...datadogRumAppOptions,
      ...initRumConfiguration,
      beforeSend: (event, context) => {
        if (!this._canSendEvent) {
          return false
        }

        /**
         * Automatically detect microfrontend service name and version from the error stack trace.
         * Please note that it works as long as microfrontends are deployed from a dedicated folder in the CDN and have unique hash per release:
         * https://cdn.sennder.cloud.com/npm/<microfrontend-name>/<optional:branch-name>/<commit-hash>/<version>.<file-hash>.js
         * This is configured in our templates and sennCI, and was stable for years. If something changes there, this logic should be updated too.
         */
        if (this._enableServiceDetection) {
          // @ts-expect-error Datadog types are too restrictive https://github.com/DataDog/browser-sdk/issues/3235#issuecomment-2573561720
          const stack = context?.handlingStack || event?.error?.stack

          const matches = stack?.match(MICROFRONTEND_REGEX)
          const service = matches?.[1]
          const version = matches?.[2]

          if (service && version) {
            event.service = service
            event.version = version
          }
        }

        return true
      },
    })
  }
}
